import {
  Button,
  Section,
  SectionHeader,
  SectionContent,
  StepperNavigation,
  SingleSelect,
  Form,
  FormLabel,
  FormControl,
  FormItem,
  Input,
  FormDescription,
  FormField,
  FormMessage,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@predoc/cow';
import { CircleX, Info } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';

import { GENDER_OPTIONS, defaultPatient } from '../../../constants';
import { ProfileSingleSelectDropdown } from '@/feature/users/components/ProfileAccessDropdown';
import { useGetCurrentUserProfileAccess } from '@/feature/users/hooks/usersApi';
import { ExistingPatient, PatientCreationStep_Patient } from '../../../types';
import { DatePickerInput } from '@/components/DatePickerInput';
import { ExistingPatientConfirm } from './ExistingPatientConfirm';
import { useFindExistingPatient } from '@/feature/patients/hooks/patientsAPI';
import { usePatientRequestContext } from '@/feature/patients/context/usePatientRequestContext';
import {
  patientSchema,
  PatientFormFieldProps,
  PatientFormType,
  NestedPatientFormFieldProps,
} from './validation';
import { ExistingPatientSearchPage } from './ExistingPatientSearchPage';
import { CityAutocomplete } from '@/components/CityAutocomplete';

export type FormData = z.infer<typeof patientSchema>;

export const PatientSection = () => {
  const { currentStepIndex, setCurrentStep, requestState, setRequestState } =
    usePatientRequestContext();
  const [existingModalOpen, setExistingModalOpen] = useState(false);
  const [existingPatient, setExistingPatient] =
    useState<ExistingPatient | null>(null);
  const [showExistingPatientSearch, setShowExistingPatientSearch] =
    useState(false);
  const { execute: findExistingPatient } = useFindExistingPatient();
  const { data: profileOptions = [] } = useGetCurrentUserProfileAccess({
    enabled: true,
  });

  const form = useForm<PatientFormType>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      ...(requestState.patient || defaultPatient),
      organizationProfile: requestState.organizationProfileId || '',
    },
  });

  // persist patient data when returning to this step
  useEffect(() => {
    if (requestState.patient) {
      form.reset({
        ...requestState.patient,
        organizationProfile: requestState.organizationProfileId || '',
      });
    }
  }, [requestState.patient, requestState.organizationProfileId, form]);

  const handleStepConfirm = useCallback(
    (data: PatientFormType | ExistingPatient, isExisting: boolean = false) => {
      // For new patients (form data), explicitly remove any existing patient ID
      // This is so that we don't end up with an ID if the user selects an existing patient initially but then changes to entering a new one
      const patientData = isExisting
        ? (data as PatientCreationStep_Patient)
        : (() => {
            // Exclude organizationProfile from form data since it's stored separately as organizationProfileId
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { organizationProfile, ...rest } = data as PatientFormType;
            return { ...rest, id: undefined };
          })();

      // Extract organizationProfileId:
      // - From existing patient's organizationProfile.id (profile ID) if using existing patient
      // - From form's organizationProfile (now just a string ID) if creating new patient
      const organizationProfileId = isExisting
        ? (data as ExistingPatient).organizationProfile?.id
        : (data as PatientFormType).organizationProfile;

      const nextOrganizationProfileId = organizationProfileId || '';
      const previousOrganizationProfileId = requestState.organizationProfileId;
      const profileChanged =
        previousOrganizationProfileId !== nextOrganizationProfileId;

      setRequestState({
        ...requestState,
        patient: patientData as PatientCreationStep_Patient,
        organizationProfileId: nextOrganizationProfileId,
        request: profileChanged
          ? {
              ...requestState.request,
              selectedRequestMethods: [],
            }
          : requestState.request,
      });

      setCurrentStep(currentStepIndex + 1);
    },
    [currentStepIndex, requestState, setCurrentStep, setRequestState],
  );

  const handleNext = useCallback(
    form.handleSubmit(async (values) => {
      try {
        const searchParams = {
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName || undefined,
          dateOfBirth: values.dateOfBirth,
          genderName: values.genderName || undefined,
          postalCode: values.address.postalCode,
          organizationProfileId: values.organizationProfile || undefined,
        };

        const result = await findExistingPatient(searchParams);

        if (result.patients?.length && result.patients.length > 0) {
          setExistingPatient(result.patients[0]);
          setExistingModalOpen(true);
          return;
        }

        // No existing patient found, proceed to next step
        handleStepConfirm(values);
      } catch (error) {
        console.error(error);
      }
    }),
    [form, findExistingPatient, handleStepConfirm],
  );

  const handleBackToPatientDetails = () => {
    setExistingModalOpen(false);
  };

  // confirm from modal
  const handleUseExistingPatient = () => {
    handleStepConfirm(existingPatient ?? ({} as ExistingPatient), true);
    setExistingModalOpen(false);
  };

  // select from search page
  const handleSelectExistingPatient = useCallback(
    (patient: ExistingPatient) => {
      handleStepConfirm(patient, true);
      setShowExistingPatientSearch(false);
    },
    [handleStepConfirm],
  );

  const handleProfileChange = useCallback(
    (profileId: string, onChange: (value: string) => void) => {
      onChange(profileId);
    },
    [],
  );

  const handleCitySelect = useCallback(
    (_city: string, state: string) => {
      // Auto-fill state when city is selected
      // City is already set via field.onChange in CityAutocomplete
      form.setValue('address.state', state, {
        shouldValidate: true,
      });
    },
    [form],
  );

  const toggleExistingPatientSearch = () => {
    setShowExistingPatientSearch((prev) => !prev);
  };

  if (showExistingPatientSearch) {
    return (
      <ExistingPatientSearchPage
        onBackToNewPatient={toggleExistingPatientSearch}
        onSelectPatient={handleSelectExistingPatient}
      />
    );
  }

  return (
    <>
      <Section className="bg-white">
        <SectionHeader>
          <h1 className="text-xl font-bold">Patient</h1>
          <Button
            variant="link"
            className="text-sm"
            onClick={toggleExistingPatientSearch}
          >
            Looking for an existing patient?
          </Button>
        </SectionHeader>
        <SectionContent>
          <Form {...form}>
            <form className="w-full space-y-6" noValidate>
              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }: PatientFormFieldProps<'firstName'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. Janet"
                          aria-label="First Name Input"
                          maxLength={45}
                        />
                      </FormControl>
                      <FormDescription>
                        {field.value?.length || 0}/45 characters
                      </FormDescription>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="middleName"
                  render={({ field }: PatientFormFieldProps<'middleName'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>Middle Initial or Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. M"
                          aria-label="Middle Initial or Name Input"
                        />
                      </FormControl>
                      <FormDescription className="invisible">
                        &nbsp;
                      </FormDescription>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }: PatientFormFieldProps<'lastName'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel className="flex items-center justify-between">
                        Last Name *
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-muted-foreground flex items-center gap-1 text-sm underline hover:cursor-pointer">
                              <Info className="h-4 w-4" />
                              former last names
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            Include former last names in the Last Name field by
                            using parentheses and commas. e.g. “Doe (Washington,
                            Avery)”
                          </TooltipContent>
                        </Tooltip>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. Doe"
                          aria-label="Last Name Input"
                          maxLength={60}
                        />
                      </FormControl>
                      <FormDescription>
                        {field.value?.length || 0}/60 characters
                      </FormDescription>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }: PatientFormFieldProps<'dateOfBirth'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Date of Birth *</FormLabel>
                      <FormControl>
                        <DatePickerInput
                          onChange={(date: Date | undefined) => {
                            field.onChange(
                              date ? format(date, 'yyyy-MM-dd') : '',
                            );
                          }}
                          value={
                            field.value ? parseISO(field.value) : undefined
                          }
                          disableDates="future"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="genderName"
                  render={({ field }: PatientFormFieldProps<'genderName'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Gender *</FormLabel>
                      <FormControl>
                        <SingleSelect
                          options={GENDER_OPTIONS}
                          handleChange={field.onChange}
                          value={field.value}
                          placeholder="Select a gender..."
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="contact.phoneNumber"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'contact.phoneNumber'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Phone Number *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          inputMode="numeric"
                          placeholder="ex. 1234567890"
                          maxLength={10}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            field.onChange(
                              e.target.value.replace(/\D/g, '').slice(0, 10),
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact.emailAddress"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'contact.emailAddress'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="ex. janetdoe@email.com"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>
              {/* Address */}
              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="address.address1"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.address1'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Street Address</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. 123 Maple Avenue"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.address2"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.address2'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Street Address Line 2</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. Unit 100"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="address.city"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.city'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <CityAutocomplete
                          value={field.value || ''}
                          onChange={field.onChange}
                          onCitySelect={handleCitySelect}
                          placeholder="ex. Greenville"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.state"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.state'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>State/Province</FormLabel>
                      <FormControl>
                        <Input {...field} type="text" placeholder="ex. NY" />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.postalCode"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.postalCode'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>Postal/ZIP Code *</FormLabel>
                      <FormControl>
                        <Input {...field} type="text" placeholder="ex. 12345" />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="organizationProfile"
                  render={({
                    field,
                  }: PatientFormFieldProps<'organizationProfile'>) => {
                    return (
                      <FormItem className="col-span-6">
                        <FormLabel>Profile *</FormLabel>
                        <FormControl>
                          <ProfileSingleSelectDropdown
                            value={field.value || ''}
                            onChange={(profileId: string) =>
                              handleProfileChange(profileId, field.onChange)
                            }
                            disabled={false}
                            options={profileOptions}
                          />
                        </FormControl>
                        <FormMessage className="text-destructive" />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </form>
          </Form>
        </SectionContent>
      </Section>
      <ExistingPatientConfirm
        open={existingModalOpen}
        handleBackToPatientDetails={handleBackToPatientDetails}
        handleUseExistingPatient={handleUseExistingPatient}
        setExistingModalOpen={setExistingModalOpen}
        patient={existingPatient}
      />
      <StepperNavigation>
        <div className="ml-auto flex gap-2">
          {form.formState.isSubmitted && form.formState.errors && (
            <div className="text-destructive flex items-center gap-2">
              <CircleX className="h-4 w-4" />
              Please fix validation errors.
            </div>
          )}
          <Button className="ml-auto" onClick={handleNext}>
            Next
          </Button>
        </div>
      </StepperNavigation>
    </>
  );
};
